/**
 * Auth API — login, register, profile, logout
 */

import apiClient, { tokenStore } from './client';

export interface LoginResponse {
    access: string;
    refresh: string;
    user: {
        id: number;
        email: string;
        full_name: string;
        role: 'customer' | 'vendor' | 'admin' | 'superadmin';
        avatar: string | null;
        country_code?: string;
        city?: string;
        store?: {
            id?: number;
            store_name?: string;
            store_name_ar?: string;
            vendor_type?: string;
            approval_status?: string;
            pos_approved?: boolean;
            pos_request_status?: string;
            freeze_selling?: boolean;
            is_blacklisted?: boolean;
            is_active?: boolean;
        } | null;
        account_status?: {
            is_authenticated?: boolean;
            is_active?: boolean;
            role?: string;
            is_admin_or_above?: boolean;
            is_vendor?: boolean;
            store_exists?: boolean;
            store_is_active?: boolean | null;
            store_is_approved?: boolean | null;
            store_is_blacklisted?: boolean | null;
            store_freeze_selling?: boolean | null;
            store_pos_approved?: boolean | null;
            store_block_pos?: boolean | null;
            can_access_vendor_portal?: boolean;
            can_sell?: boolean;
            can_use_pos?: boolean;
            restrictions?: string[];
        };
    };
}

export interface RegisterData {
    email: string;
    password: string;
    full_name: string;
    phone?: string;
    country_code?: string;
    role?: 'customer' | 'vendor';
}

export interface SocialAuthData {
    provider: 'google' | 'apple' | 'facebook' | 'icloud';
    role?: 'customer' | 'vendor' | 'showroom';
    email?: string;
    full_name?: string;
    phone?: string;
    country_code?: string;
    city?: string;
    vendor_type?: string;
    store_name?: string;
    store_name_ar?: string;
}

const LOCAL_API_PORT = '8000';

function isLocalHost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

function resolveBackendOrigin(): string {
    const raw = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    try {
        const parsed = new URL(raw);
        if (typeof window !== 'undefined') {
            const runtimeHost = window.location.hostname;
            if (isLocalHost(parsed.hostname) && !isLocalHost(runtimeHost)) {
                parsed.hostname = runtimeHost;
                parsed.port = LOCAL_API_PORT;
            }
        }
        return parsed.origin;
    } catch {
        return raw;
    }
}

export const authApi = {
    login: async (email: string, password: string): Promise<LoginResponse> => {
        const { data } = await apiClient.post<LoginResponse>('/auth/login/', { email, password });
        tokenStore.set(data.access, data.refresh);
        return data;
    },

    register: async (payload: RegisterData): Promise<LoginResponse> => {
        const { data } = await apiClient.post<LoginResponse>('/auth/register/', payload);
        tokenStore.set(data.access, data.refresh);
        return data;
    },

    socialAuth: async (
        payload: SocialAuthData
    ): Promise<LoginResponse & { provider?: string; is_new_user?: boolean }> => {
        const { data } = await apiClient.post<LoginResponse & { provider?: string; is_new_user?: boolean }>(
            '/auth/social-auth/',
            payload,
            { skipAuth: true } as any
        );
        tokenStore.set(data.access, data.refresh);
        return data;
    },

    startSocialOAuth: (payload: SocialAuthData & { next?: string }) => {
        if (typeof window === 'undefined') return;

        const providerPath = payload.provider;
        const params = new URLSearchParams();
        params.set('role', payload.role || 'customer');
        if (payload.full_name) params.set('full_name', payload.full_name);
        if (payload.email) params.set('email', payload.email);
        if (payload.phone) params.set('phone', payload.phone);
        if (payload.country_code) params.set('country_code', payload.country_code);
        if (payload.city) params.set('city', payload.city);
        if (payload.vendor_type) params.set('vendor_type', payload.vendor_type);
        if (payload.store_name) params.set('store_name', payload.store_name);
        if (payload.store_name_ar) params.set('store_name_ar', payload.store_name_ar);
        if (payload.next) params.set('next', payload.next);

        const backendOrigin = resolveBackendOrigin();
        const startUrl = `${backendOrigin}/api/auth/oauth/${providerPath}/start/?${params.toString()}`;
        window.location.assign(startUrl);
    },

    logout: async () => {
        try {
            const refresh = tokenStore.getRefresh();
            if (refresh) await apiClient.post('/auth/logout/', { refresh });
        } finally {
            tokenStore.clear();
        }
    },

    getMe: async () => {
        const { data } = await apiClient.get('/auth/me/');
        return data;
    },

    updateMe: async (payload: Partial<RegisterData & { avatar: File }>) => {
        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
            if (v !== undefined) formData.append(k, v as string | Blob);
        });
        const { data } = await apiClient.patch('/auth/me/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    },

    // Vendor store management
    getMyStore: async () => {
        const { data } = await apiClient.get('/auth/stores/');
        return data.results?.[0] || null;
    },

    createStore: async (payload: object) => {
        const { data } = await apiClient.post('/auth/stores/', payload);
        return data;
    },

    requestPOSAccess: async (reason: string) => {
        const { data } = await apiClient.post('/auth/pos-requests/', { reason });
        return data;
    },
};
